import type { Document } from "@components/documents/types";
import { FailedPriorArtAlert } from "@components/officeActions/FailedPriorArtAlert";
import { OfficeActionNotFoundModal } from "@components/officeActions/OfficeActionNotFoundModal";
import { useLoadOfficeAction, type PriorArt } from "@components/officeActions/useOfficeAction";
import { OFFICE_ACTION } from "@constants/documentCategories";
import { Toaster, useId } from "@fluentui/react-components";
import { useDocumentContext } from "@hooks/useDocumentContext";
import { useSessions, type Session } from "@hooks/useSessions";
import { useToast } from "@hooks/useToast";
import { reportError } from "@utils/observability/reportError";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { SessionCreationFlowContext } from "./SessionCreationFlowContext";
import type { SessionCreationFlow } from "./types";

type Props = {
  children: React.ReactNode;
};

type OaNotFoundState = {
  session: Session;
  applicationId: string;
};

export const SessionCreationFlowProvider: React.FC<Props> = ({ children }) => {
  const { createSession, setCurrentSession } = useSessions();
  const { seedContextDocuments } = useDocumentContext();
  const loadOfficeAction = useLoadOfficeAction();
  const toasterId = useId("session-creation-toast");
  const { showToast } = useToast(toasterId);

  const [failedPriorArt, setFailedPriorArt] = useState<PriorArt[] | null>(null);
  const [oaNotFound, setOaNotFound] = useState<OaNotFoundState | null>(null);

  const finalize = useCallback(
    (session: Session, extraDocs: Document[]) => {
      seedContextDocuments(extraDocs, session.session_id);
      setCurrentSession(session);
    },
    [seedContextDocuments, setCurrentSession]
  );

  const submit = useCallback<SessionCreationFlow["submit"]>(
    async (form) => {
      const { mode, name } = form;

      // 1) Create the session. If this fails, the modal stays open with the
      //    user's input intact and the host is never notified.
      let createdSession: Session;
      try {
        createdSession = await createSession.mutateAsync({ name, mode });
      } catch (error) {
        reportError("session.create", error, { name, mode });
        showToast({
          title: "Error",
          message: ERROR_MESSAGES.createSession,
          intent: "error",
          timeout: ERROR_TOAST_TIMEOUT,
        });
        throw error;
      }

      // 2) For Office Action sessions with an Application Number, run the OA
      //    upload before finalizing — the returned `autoContextDocuments`
      //    feed the seed, and by the time the host's `setCurrentSession`-
      //    driven docs fetch runs, the backend already has the prior art
      //    uploaded. An empty applicationId means "create blank project" →
      //    skip the OA call entirely. Failures are partial: the session
      //    stays. A 404 (application id not found) opens the retry modal
      //    and defers the ready handoff; other errors surface via toast
      //    and fall through to the default (no prior art) finalize below.
      if (form.mode === OFFICE_ACTION && form.oaParams.applicationId) {
        try {
          const result = await loadOfficeAction.mutateAsync({
            sessionId: createdSession.session_id,
            applicationId: form.oaParams.applicationId,
          });
          if (result.failedPriorArt.length > 0) {
            setFailedPriorArt(result.failedPriorArt);
          }
          finalize(createdSession, result.autoContextDocuments);
          return createdSession;
        } catch (error) {
          reportError("officeAction.load", error, {
            sessionId: createdSession.session_id,
            applicationId: form.oaParams.applicationId,
          });
          if (isHttpStatus(error, 404)) {
            setOaNotFound({ session: createdSession, applicationId: form.oaParams.applicationId });
            return createdSession;
          }
          showToast({
            title: "Warning",
            message: ERROR_MESSAGES.loadOfficeAction,
            intent: "warning",
            timeout: ERROR_TOAST_TIMEOUT,
          });
        }
      }

      // 3) Default path: non-OA, OA-skipped (blank applicationId), or
      //    OA-error-non-404. Seed with just the current document.
      finalize(createdSession, []);
      return createdSession;
    },
    [createSession, loadOfficeAction, finalize, showToast]
  );

  const handleNotFoundRetry = useCallback(
    async (applicationId: string) => {
      if (!oaNotFound) return;
      const { session } = oaNotFound;
      try {
        const result = await loadOfficeAction.mutateAsync({
          sessionId: session.session_id,
          applicationId,
        });
        setOaNotFound(null);
        if (result.failedPriorArt.length > 0) {
          setFailedPriorArt(result.failedPriorArt);
        }
        finalize(session, result.autoContextDocuments);
      } catch (error) {
        reportError("officeAction.load", error, { sessionId: session.session_id, applicationId });
        if (isHttpStatus(error, 404)) {
          // Still not found — keep the modal open with the user's input.
          // (oaNotFound stays truthy, so the modal stays mounted.)
          return;
        }
        // Any other failure: close the modal, surface a toast, and proceed
        // to navigation so the user is not stuck on a dead modal.
        setOaNotFound(null);
        showToast({
          title: "Warning",
          message: ERROR_MESSAGES.loadOfficeAction,
          intent: "warning",
          timeout: ERROR_TOAST_TIMEOUT,
        });
        finalize(session, []);
      }
    },
    [oaNotFound, loadOfficeAction, finalize, showToast]
  );

  const handleNotFoundCancel = useCallback(() => {
    if (!oaNotFound) return;
    const { session } = oaNotFound;
    setOaNotFound(null);
    finalize(session, []);
  }, [oaNotFound, finalize]);

  // Note: `submit`'s `useCallback` deps include `createSession` (the
  // `UseMutationResult` object) whose identity changes on every state tick of
  // the mutation (isPending/status/etc.). That invalidates `submit`, which
  // invalidates `value`, which re-renders all context consumers. Today the
  // only consumer is the new-session modal that mounts on demand, so this is
  // harmless; if more consumers ever subscribe, narrow the deps to stable
  // pieces (`createSession.mutateAsync`).
  const value = useMemo<SessionCreationFlow>(
    () => ({ submit, isCreatingSession: createSession.isPending }),
    [submit, createSession.isPending]
  );

  return (
    <SessionCreationFlowContext.Provider value={value}>
      {children}
      <Toaster toasterId={toasterId} position="bottom" />
      {failedPriorArt && <FailedPriorArtAlert priorArt={failedPriorArt} onClose={() => setFailedPriorArt(null)} />}
      {oaNotFound && (
        <OfficeActionNotFoundModal
          initialApplicationId={oaNotFound.applicationId}
          isRetrying={loadOfficeAction.isPending}
          onRetry={handleNotFoundRetry}
          onCancel={handleNotFoundCancel}
        />
      )}
    </SessionCreationFlowContext.Provider>
  );
};

const isHttpStatus = (error: unknown, status: number): boolean =>
  typeof error === "object" && error !== null && "status" in error && (error as { status: unknown }).status === status;

const ERROR_TOAST_TIMEOUT = 4000;

const ERROR_MESSAGES = {
  createSession: "An unexpected error occurred while creating the project. Please try again.",
  loadOfficeAction:
    "The project was created, but the Office Action could not be loaded. Please check the Application ID.",
} as const;
